import lodashSnakeCase from 'lodash-es/snakeCase';

import { eventTypeStandard, getPageType, push } from './internal/core';
import { pushWithGlobalSale } from './internal/pushWithGlobal';
import { formatSale } from './internal/saleFormat';
import { useTrackingReferralListName } from './internal/state';
import { attachVideoTracker } from './internal/video';

import { apiDirectSaleDetail, AuctionState, DirectSaleState } from '~/apiClient';
import { isAuction, isDirectSale, type PlatformSaleItemEntity } from '~/types/sale.type';

type GalleryType = 'photo' | 'video';
type GalleryContent = 'main' | 'details';
type AlertType = 'expired_revision';

export type UseTrackingSdpReturn = {
    trackViewSale: (sale: PlatformSaleItemEntity) => void;
    trackGalleryView: (galleryType: GalleryType, galleryContent: GalleryContent) => void;
    trackFileDownload: (fileName: string, fileType: string) => void;
    trackSelectSale: VoidFunction;
    trackFeeLink: VoidFunction;

    trackAdpEmailLink: VoidFunction;
    trackAdpConfirmButton: VoidFunction;
    trackAdpAlertView: (alertType: AlertType) => void;
    trackAdpPlaceBid: VoidFunction;

    trackDsdpConfirmButton: VoidFunction;

    trackSalesContactWhatsappButton: VoidFunction;
    trackSalesContactPhoneLink: (phone: string) => void;
    trackSalesContactOpenContactForm: VoidFunction;
    trackSalesContactExitContactFormModal: VoidFunction;
    trackSalesContactFocusContactFormField: (fieldName: string) => void;
    trackSalesContactContactFormValidationError: (errorType: string) => void;
    trackSalesContactSubmitContactForm: VoidFunction;
    trackSalesContactContactFormSent: (email: string, phone: string) => void;

    attachSdpVideoTracker: (video: HTMLVideoElement) => VoidFunction;

    trackDsdpOrderModalConfirm: VoidFunction;
    trackDsdpOrderModalClose: VoidFunction;

    trackDirectSaleOrderPlaced: (directSaleId: string) => Promise<void>;
    trackDirectSaleOfferPlaced: VoidFunction;
};

const getSaleUserVisualization = (sale: PlatformSaleItemEntity): string | null => {
    const { data: session } = useAuthUtils();

    if (!isAuction(sale)) return null;

    if (!sale.negotiation || !session.value) return 'neutral';

    return sale.negotiation.buyerCompanyId === session.value.user?.companyId ? 'buyer_negotiator' : 'seller_negotiator';
};

const formatSalePageStatus = (sale: PlatformSaleItemEntity): string => {
    if (isAuction(sale)) {
        if ([AuctionState.Draft, AuctionState.ToBeScheduled, AuctionState.ToBeApproved, AuctionState.Waiting].includes(sale.state)) return 'draft';

        if ([AuctionState.Pending, AuctionState.Running, AuctionState.Expired].includes(sale.state)) return 'published';
    } else if (isDirectSale(sale)) {
        if ([DirectSaleState.Draft, DirectSaleState.ToBeScheduled, DirectSaleState.ToBeApproved, DirectSaleState.Waiting].includes(sale.state))
            return 'draft';

        if ([DirectSaleState.Pending, DirectSaleState.Open, DirectSaleState.Closed].includes(sale.state)) return 'published';
    }

    return lodashSnakeCase(sale.state);
};

export const useTrackingSdp = (): UseTrackingSdpReturn => {
    const referralListName = useTrackingReferralListName();

    return {
        trackViewSale(sale): void {
            push({
                event: 'view_sale',
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                user_visualization: getSaleUserVisualization(sale),
                page_status: formatSalePageStatus(sale),
                ...eventTypeStandard,
            });
        },

        trackGalleryView(galleryType, galleryContent): void {
            pushWithGlobalSale((sales, sale) => ({
                event: 'gallery_view',
                gallery_type: galleryType,
                gallery_content: galleryContent,
                page_type: getPageType(),
                sales,
                user_visualization: getSaleUserVisualization(sale),
                page_status: formatSalePageStatus(sale),
                ...eventTypeStandard,
            }));
        },

        trackFileDownload(fileName, fileType): void {
            pushWithGlobalSale(sales => ({
                event: 'file_download',
                file_type: fileType,
                file_name: fileName,
                page_type: getPageType(),
                sales,
                ...eventTypeStandard,
            }));
        },

        trackFeeLink(): void {
            pushWithGlobalSale(sales => ({
                event: 'click',
                click_element: 'link',
                click_text: 'what_are_they',
                page_type: getPageType(),
                sales,
                ...eventTypeStandard,
            }));
        },

        trackSelectSale(): void {
            pushWithGlobalSale(sales => ({
                event: 'select_sale',
                list_name: 'related_vehicles',
                page_type: getPageType(),
                sales,
                ...eventTypeStandard,
            }));
        },

        trackAdpEmailLink(): void {
            pushWithGlobalSale(sales => ({
                event: 'click',
                page_type: getPageType(),
                sales,
                ...eventTypeStandard,
            }));
        },

        trackAdpConfirmButton(): void {
            pushWithGlobalSale(sales => ({
                event: 'click',
                click_element: 'button',
                click_text: 'confirm_auction',
                page_type: getPageType(),
                sales,
                ...eventTypeStandard,
            }));
        },

        trackAdpAlertView(alertType): void {
            pushWithGlobalSale(sales => ({
                event: 'alert_view',
                alert_type: alertType,
                page_type: getPageType(),
                sales,
                ...eventTypeStandard,
            }));
        },

        trackAdpPlaceBid(): void {
            pushWithGlobalSale(sales => ({
                event: 'click',
                click_element: 'button',
                click_text: 'place_bid',
                page_type: getPageType(),
                sales,
                ...eventTypeStandard,
            }));
        },

        trackDsdpConfirmButton(): void {
            pushWithGlobalSale(sales => ({
                event: 'click',
                click_element: 'button',
                click_text: 'confirm_direct_sale',
                page_type: getPageType(),
                sales,
                ...eventTypeStandard,
            }));
        },

        trackSalesContactWhatsappButton(): void {
            pushWithGlobalSale(sales => ({
                event: 'click',
                click_element: 'button',
                click_text: 'whatsapp',
                page_type: getPageType(),
                sales,
                ...eventTypeStandard,
            }));
        },

        trackSalesContactPhoneLink(phone): void {
            pushWithGlobalSale(sales => ({
                event: 'click',
                click_element: 'phone',
                click_text: phone,
                page_type: getPageType(),
                sales,
                ...eventTypeStandard,
            }));
        },

        trackSalesContactOpenContactForm(): void {
            pushWithGlobalSale(sales => ({
                event: 'click',
                click_element: 'button',
                click_text: 'contact_us',
                page_type: getPageType(),
                sales,
                ...eventTypeStandard,
            }));
        },

        trackSalesContactExitContactFormModal(): void {
            pushWithGlobalSale(sales => ({
                event: 'modal_exit',
                page_type: 'contact_sales_rep',
                sales,
                ...eventTypeStandard,
            }));
        },

        trackSalesContactFocusContactFormField(fieldName): void {
            pushWithGlobalSale(sales => ({
                event: 'click',
                click_element: 'field',
                click_text: lodashSnakeCase(fieldName),
                page_type: 'contact_sales_rep',
                sales,
                ...eventTypeStandard,
            }));
        },

        trackSalesContactContactFormValidationError(errorType): void {
            pushWithGlobalSale(sales => ({
                event: 'form_error',
                error_type: errorType,
                page_type: 'contact_sales_rep',
                sales,
                ...eventTypeStandard,
            }));
        },

        trackSalesContactSubmitContactForm(): void {
            pushWithGlobalSale(sales => ({
                event: 'click',
                click_element: 'button',
                click_text: 'send_request',
                page_type: 'contact_sales_rep',
                sales,
                ...eventTypeStandard,
            }));
        },

        trackSalesContactContactFormSent(email, phone): void {
            pushWithGlobalSale(sales => ({
                event: 'generate_lead',
                page_type: 'contact_sales_rep',
                user_email: email,
                user_phone: phone || null,
                sales,
                ...eventTypeStandard,
            }));
        },

        attachSdpVideoTracker(video): VoidFunction {
            const filename = video.src.split('/').pop();
            const videoName = filename ? lodashSnakeCase(filename.split('.').slice(0, -1).join('.')) : null;

            return attachVideoTracker(video, {
                onPlay() {
                    pushWithGlobalSale(sales => ({
                        event: 'video_start',
                        page_type: getPageType(),
                        video_name: videoName,
                        sales,
                        ...eventTypeStandard,
                    }));
                },
                onProgress(percent) {
                    pushWithGlobalSale(sales => ({
                        event: 'video_progress',
                        page_type: getPageType(),
                        video_name: videoName,
                        video_percent: percent,
                        sales,
                        ...eventTypeStandard,
                    }));
                },
                onEnded() {
                    pushWithGlobalSale(sales => ({
                        event: 'video_complete',
                        page_type: getPageType(),
                        video_name: videoName,
                        sales,
                        ...eventTypeStandard,
                    }));
                },
            });
        },

        trackDsdpOrderModalConfirm(): void {
            pushWithGlobalSale(sales => ({
                event: 'click',
                click_element: 'button',
                click_text: 'buy_now',
                page_type: 'place_order',
                sales,
                ...eventTypeStandard,
            }));
        },

        trackDsdpOrderModalClose(): void {
            pushWithGlobalSale(sales => ({
                event: 'click',
                click_element: 'button',
                click_text: 'close',
                page_type: 'place_order',
                sales,
                ...eventTypeStandard,
            }));
        },

        async trackDirectSaleOrderPlaced(directSaleId): Promise<void> {
            const sale = await apiDirectSaleDetail(directSaleId);

            push({
                event: 'order_placed',
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackDirectSaleOfferPlaced(): void {
            pushWithGlobalSale(sales => ({
                event: 'offer_placed',
                sales,
                ...eventTypeStandard,
            }));
        },
    };
};
